import React, { useRef, useEffect, useState } from 'react';
import { Menu, PanelLeft, RotateCcw, LogIn, Download, Folder, Sparkles, Bell, LayoutGrid } from 'lucide-react';
import { User, GlobalConfig, PromoCode, Message, MessageDensity } from '../../types';
// HeaderTokenTimer removed - now displayed in ProfileDropdown
import ChatSearchBar from '../ChatSearchBar';
import ProfileDropdown from '../profile/ProfileDropdown';
import PWAInstallGuide from '../PWAInstallGuide';
// GamificationWidget removed from header - now in ProfileDropdown
import { getRankGradient, getRankColor, getRankName } from '../../utils/rankUtils';
import { installPWA, canInstall } from '../../utils/pwaInstaller';
import UserStatusWidget from '../UserStatusWidget';

interface HeaderProps {
    // User & Config
    user: User | null;
    config: GlobalConfig;
    promoCodes: PromoCode[];

    // Theme & Settings
    isDarkMode: boolean;
    messageDensity: MessageDensity;

    // Auto Budget Settings
    autoBudgetEnabled: boolean;
    budgetPerRequest: number;

    // State
    messages: Message[];
    isSidebarOpen: boolean;
    isProfileMenuOpen: boolean;

    // Handlers
    onToggleSidebar: () => void;
    onOpenMobileMenu: () => void;
    onOpenRefreshModal: () => void;
    onOpenUsageModal: () => void;
    onOpenAuthModal: () => void;
    onOpenExtendPremium: () => void;
    onOpenTokenPurchase: () => void;
    onToggleDarkMode: () => void;
    onSetDensity: (density: MessageDensity) => void;
    onLogout: () => void;
    onRedeemCode: (code: string) => Promise<{ success: boolean; message?: string; error?: string }>;
    onOpenSupport?: () => void;
    onToggleAutoBudget: (enabled: boolean) => void;
    onSetBudgetPerRequest: (budget: number) => void;
    onUserUpdate: (user: User) => void;
    onProfileMenuToggle: (open: boolean) => void;
    onJumpToMessage: (messageId: string) => void;
    onExportAll?: () => void; // Open export modal for compiled export
    onOpenSessionFiles?: () => void; // Open session files modal
    onOpenSocial?: () => void; // Open social modal
    onOpenNotifications?: () => void; // Open notifications modal
    onOpenQuizConfig?: () => void;
    onOpenMockExamConfig?: () => void;
    onOpenGamification?: () => void;
    onOpenDashboard?: () => void;
    onOpenReferral?: () => void;
    showToast: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

const Header: React.FC<HeaderProps> = ({
    user,
    config,
    promoCodes,
    isDarkMode,
    messageDensity,
    autoBudgetEnabled,
    budgetPerRequest,
    messages,
    isSidebarOpen,
    isProfileMenuOpen,
    onToggleSidebar,
    onOpenMobileMenu,
    onOpenRefreshModal,
    onOpenUsageModal,
    onOpenAuthModal,
    onOpenExtendPremium,
    onOpenTokenPurchase,
    onToggleDarkMode,
    onSetDensity,
    onLogout,
    onRedeemCode,
    onOpenSupport,
    onToggleAutoBudget,
    onSetBudgetPerRequest,
    onUserUpdate,
    onProfileMenuToggle,
    onJumpToMessage,
    onExportAll,
    onOpenSessionFiles,
    onOpenSocial,
    onOpenNotifications,
    onOpenQuizConfig,
    onOpenMockExamConfig,
    onOpenGamification,
    onOpenDashboard,
    onOpenReferral,
    showToast
}) => {
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [showInstallGuide, setShowInstallGuide] = useState(false);

    // Close profile menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                onProfileMenuToggle(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onProfileMenuToggle]);

    return (
        <header
            className={`
                h-[60px] sm:h-[68px] border-b flex items-center justify-between px-3 sm:px-6 lg:px-8 
                z-30 flex-shrink-0 backdrop-blur-xl sticky top-0 transition-colors ios-header-safe
                ${isDarkMode
                    ? 'bg-slate-900/95 border-slate-800/50 shadow-xl shadow-black/20'
                    : 'bg-white/95 border-slate-200/50 shadow-sm'
                }
            `}
            style={{ paddingTop: 'max(0px, env(safe-area-inset-top, 0px))' }}
            role="banner"
        >
            {/* Left Section - Menu & Logo */}
            <div className="flex items-center gap-6 flex-1 min-w-0">
                {/* Mobile Menu Button */}
                <button
                    onClick={onOpenMobileMenu}
                    className={`md:hidden p-2 rounded-lg transition-all ${isDarkMode
                        ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                    aria-label="Open menu"
                >
                    <Menu size={22} />
                </button>

                {/* Desktop Sidebar Toggle */}
                <button
                    onClick={onToggleSidebar}
                    className={`hidden md:flex p-2 rounded-lg transition-all ${isDarkMode
                        ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                    aria-label="Toggle sidebar"
                >
                    <PanelLeft size={22} />
                </button>

                {/* Logo & Title - Enhanced with smooth animations */}
                <div className="flex items-center gap-3 group">
                    <div className={`
                        hidden sm:block relative w-11 h-11 rounded-2xl overflow-hidden 
                        ring-2 ring-blue-500/20 shadow-lg
                        transition-all duration-300 ease-out transform-gpu
                        hover:ring-blue-500/40 hover:shadow-xl hover:scale-105 
                        active:scale-95 cursor-pointer
                        ${isDarkMode ? 'bg-slate-800' : 'bg-white'}
                    `}>
                        <img
                            src="/robot-luca.png"
                            alt="Luca"
                            className="w-full h-full object-contain p-0.5 transition-transform duration-300 group-hover:scale-105"
                        />
                    </div>
                    <div className="hidden sm:block">
                        <h1 className={`font-bold text-lg tracking-tight transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Luca
                        </h1>
                        <p className={`text-xs font-medium transition-colors ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Your Personal Reviewer
                        </p>
                    </div>
                </div>

                {/* Refresh Context Button - HIDDEN */}
                {/* <button
                    onClick={onOpenRefreshModal}
                    className={`p-2 rounded-lg transition-all ml-2 hidden xl:flex ${isDarkMode
                            ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-800'
                            : 'text-slate-600 hover:text-blue-600 hover:bg-slate-100'
                        }`}
                    title="Refresh Context"
                >
                    <RotateCcw size={18} />
                </button> */}
            </div>

            {/* Right Section - Search, Tokens, Profile */}
            <div className="flex items-center gap-4">
                {/* User Status Widget (Dashboard Entry) - Full widget on desktop, icon on mobile */}
                {onOpenDashboard && user && (
                    <>
                        <button
                            onClick={onOpenDashboard}
                            className={`md:hidden p-2 rounded-lg transition-all ${isDarkMode
                                ? 'text-indigo-400 hover:text-indigo-300 hover:bg-slate-800'
                                : 'text-indigo-600 hover:text-indigo-500 hover:bg-slate-100'
                            }`}
                            aria-label="Open dashboard"
                            title="Dashboard"
                        >
                            <LayoutGrid size={20} />
                        </button>
                        <div className="hidden md:block">
                            <UserStatusWidget
                                user={user}
                                xp={user.totalXP || 0}
                                streak={user.streak || 0}
                                onClick={onOpenDashboard}
                                isDarkMode={isDarkMode}
                            />
                        </div>
                    </>
                )}

                {/* Chat Search Bar */}
                {messages.length > 0 && (
                    <ChatSearchBar
                        messages={messages}
                        isDarkMode={isDarkMode}
                        onResultSelect={onJumpToMessage}
                    />
                )}

                {/* User Profile / Sign In */}
                {user ? (
                    <div className="relative" ref={profileMenuRef}>
                        <button
                            onClick={() => onProfileMenuToggle(!isProfileMenuOpen)}
                            className="relative flex items-center gap-2.5 group/profile"
                        >
                            <div className="relative">
                                <div
                                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center text-sm font-semibold text-white
                                        ring-2 ring-offset-2 transition-all duration-200
                                        group-hover/profile:ring-offset-1 group-hover/profile:shadow-lg group-hover/profile:scale-105
                                        active:scale-95
                                        ${isDarkMode ? 'ring-offset-slate-900' : 'ring-offset-white'}
                                    `}
                                    style={{
                                        background: getRankColor(user, config),
                                        ['--tw-ring-color' as any]: getRankColor(user, config) + '60',
                                    }}
                                >
                                    {(user.username || 'U')[0].toUpperCase()}
                                </div>
                                {/* Online indicator */}
                                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900" />
                            </div>
                            <div className="hidden lg:block text-left">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {user.username}
                                </p>
                                <p
                                    className="text-xs font-semibold"
                                    style={{ color: getRankColor(user, config) }}
                                >
                                    {getRankName(user, config)}
                                </p>
                            </div>
                        </button>

                        {isProfileMenuOpen && (
                            <ProfileDropdown
                                user={user}
                                config={config}
                                isDarkMode={isDarkMode}
                                messageDensity={messageDensity}
                                promoCodes={promoCodes}
                                autoBudgetEnabled={autoBudgetEnabled}
                                budgetPerRequest={budgetPerRequest}
                                onClose={() => onProfileMenuToggle(false)}
                                onToggleDarkMode={onToggleDarkMode}
                                onSetDensity={onSetDensity}
                                onOpenUsageModal={onOpenUsageModal}
                                onOpenExtendPremium={onOpenExtendPremium}
                                onOpenTokenPurchase={onOpenTokenPurchase}
                                onLogout={onLogout}
                                onRedeemCode={onRedeemCode}
                                onOpenSupport={onOpenSupport}
                                onToggleAutoBudget={onToggleAutoBudget}
                                onSetBudgetPerRequest={onSetBudgetPerRequest}
                                onUserUpdate={onUserUpdate}
                                onOpenInstallGuide={() => setShowInstallGuide(true)}
                                onOpenSocial={onOpenSocial}
                                onOpenQuizConfig={onOpenQuizConfig}
                                onOpenMockExamConfig={onOpenMockExamConfig}
                                onOpenGamification={onOpenGamification}
                                onOpenNotifications={onOpenNotifications}
                                onOpenDashboard={onOpenDashboard}
                                onOpenReferral={onOpenReferral}
                                showToast={showToast}
                            />
                        )}
                    </div>
                ) : (
                    <button
                        onClick={onOpenAuthModal}
                        className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center gap-2
                            bg-gradient-to-r from-blue-600 to-blue-800 text-white
                            hover:from-blue-500 hover:to-blue-700 hover:shadow-lg hover:shadow-blue-600/25
                            active:scale-95
                        `}
                    >
                        <LogIn size={16} /> <span className="hidden sm:inline">Sign In</span>
                    </button>
                )}
            </div>

            {/* PWA Install Guide Modal */}
            <PWAInstallGuide
                isVisible={showInstallGuide}
                onClose={() => setShowInstallGuide(false)}
                isDarkMode={isDarkMode}
            />
        </header>
    );
};

export default Header;
